import type { LocalReminder, NotificationScheduler } from "./types";

/**
 * NotificationScheduler backed by the Capacitor Local Notifications plugin, used
 * only in the native build. The plugin is imported dynamically so the web bundle
 * never pulls it into its main chunk.
 */
export class CapacitorNotificationScheduler implements NotificationScheduler {
  async permission(): Promise<PermissionState> {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    return toPermissionState((await LocalNotifications.checkPermissions()).display);
  }

  async requestPermission(): Promise<PermissionState> {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    return toPermissionState((await LocalNotifications.requestPermissions()).display);
  }

  async schedule(reminders: LocalReminder[]): Promise<void> {
    await this.cancelAll();
    if (reminders.length === 0) return;
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    await LocalNotifications.schedule({
      notifications: reminders.map((reminder) => {
        const [hour, minute] = reminder.time.split(":").map((part) => Number(part));
        return {
          id: reminder.id,
          title: reminder.title,
          body: reminder.body,
          schedule: { on: { hour, minute }, allowWhileIdle: true },
        };
      }),
    });
  }

  async cancelAll(): Promise<void> {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    const pending = await LocalNotifications.getPending();
    if (pending.notifications.length > 0) {
      await LocalNotifications.cancel({ notifications: pending.notifications.map((n) => ({ id: n.id })) });
    }
  }

  async pending(): Promise<LocalReminder[]> {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    return (await LocalNotifications.getPending()).notifications.map((n) => {
      const on = n.schedule?.on;
      const time = on ? `${String(on.hour ?? 0).padStart(2, "0")}:${String(on.minute ?? 0).padStart(2, "0")}` : "";
      return { id: n.id, time, title: n.title, body: n.body };
    });
  }
}

/** The plugin reports "prompt-with-rationale" too; fold it into the web PermissionState set. */
function toPermissionState(display: string): PermissionState {
  if (display === "granted") return "granted";
  if (display === "denied") return "denied";
  return "prompt";
}
